import type { GdprSettings } from '@/providers/Cookie/types';
import { getLogger } from '@/lib/logger';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
  }
}

type ConsentState = 'granted' | 'denied';

interface GtmConsentConfig extends Record<string, unknown> {
  analytics_storage: ConsentState;
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  functionality_storage: ConsentState;
  security_storage: ConsentState;
}

/**
 * Initialize GTM Consent Mode with all optional categories set to "denied".
 *
 * This MUST run before the GTM container loads so that no marketing/analytics
 * tags fire before the user has given consent. GtmTracker.init() calls this
 * immediately before TagManager.initialize().
 */
export function initializeGtmConsent(): void {
  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
}

/**
 * Update GTM Consent Mode based on cookie preferences.
 */
export function updateGtmConsent(consents: GdprSettings['consents']): void {
  if (!window.gtag) {
    const logger = getLogger();
    logger.warn('gtag is not available. Make sure GTM is initialized.');
    return;
  }

  const consentConfig: GtmConsentConfig = {
    ad_storage: consents.marketing ? 'granted' : 'denied',
    ad_user_data: consents.marketing ? 'granted' : 'denied',
    ad_personalization: consents.personalization ? 'granted' : 'denied',
    analytics_storage: consents.analytics ? 'granted' : 'denied',
    functionality_storage: consents.essential ? 'granted' : 'denied',
    security_storage: consents.essential ? 'granted' : 'denied',
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'update_consent',
    consent: consentConfig,
  });

  window.gtag('consent', 'update', consentConfig);
}
